import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { NetworkStatus } from '@apollo/client';
import { tokens } from '@companycam/slab';
import { Button, Spinner } from '@companycam/slab-web';
import PropTypes from 'prop-types';

import { translate } from 'translations/i18n';
import GroupingHeader from './GroupingHeader';
import { fetchProject } from 'actions';
import { graphQLIdFromProjectDBID } from 'helpers/graphql-utils';
import { useModal } from 'components/shared/hooks';
import { Modal } from 'components/core/modal/constants';
import ProjectSubpage from '../subpages/ProjectSubpage';
import useProjectUsers from './hooks/useProjectUsers';
import useProjectUserDelete from './hooks/useProjectUserDelete';
import ProjectUser from './ProjectUser';
import Alert from 'components/shared/DesignSystem/Alert';
import ListItemAddButton from '../../shared/DesignSystem/ListItemAddButton';
import { useCurrentUser } from 'javascripts/hooks';
import {
  hasRoleAccess,
  RESTRICTED,
  STANDARD,
  ADMIN,
  MANAGER,
} from 'helpers/roles';
import { USER_DELETED_STATUS } from './constants';
import toast from 'helpers/toast';
import useProjectUserCreate from './hooks/useProjectUserCreate';
import { useModalToast } from 'components/shared/DesignSystem/ModalToast/useModalToast';
import Steven, { ELEMENT_TYPES } from 'helpers/steven';

// #region styled-components
const Layout = styled.div``;
const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--cc_size_spacing_xl);
  margin-block-end: var(--cc_size_spacing_l);
`;
const Intro = styled.div`
  flex: 1;
  min-width: 1px;
  font-size: var(--cc_size_text_m);
`;
const CollabCallout = styled.div`
  flex-basis: 240px;
  flex-shrink: 0;
  font-size: var(--cc_size_text_s);
  color: var(--cc_color_text_subtle);
  text-align: end;
`;
const CollabCalloutLink = styled.a`
  display: inline-block;
  white-space: nowrap;
  font-weight: 500;
  &:hover {
    color: var(--cc_color_link_primary_darken_15);
  }
`;
const CollabCalloutLinkTooltip = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--cc_size_spacing_xs);
  margin-block-start: var(--cc_size_spacing_xxs);
`;
const CollabCalloutTooltip = styled.span`
  text-align: start;
`;
const CollabCalloutInfoIcon = styled.i`
  color: var(--cc_color_text_nonessential);
  font-size: 18px;
`;
const ErrorContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;
const ErrorMessage = styled.div`
  flex: 1;
  padding-right: ${tokens.size_spacing_m}px;
`;
const ErrorAction = styled.div`
  flex-shrink: 0;
`;
const ProjectUserList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;
const ProjectUserListItem = styled.li`
  list-style-type: none;
`;
const AssignUserActionAddAvatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--cc_size_avatar_l);
  height: var(--cc_size_avatar_l);
  border-radius: 100%;
  border: var(--cc_size_border_width_s) dashed var(--cc_color_link_primary);
`;
const AssignUserActionIcon = styled.i`
  font-size: var(--cc_size_text_l);
`;
const LoadMoreContainer = styled.div`
  padding-top: ${tokens.size_spacing_m}px;
  text-align: center;
`;
// #endregion

/**
 * The isModal prop, and associated conditional display logic farther down in this component, is present
 * while we're potentially displaying this content both as a screen and inside of a condensed modal. isModal is also passed
 * down to children like that fruitcake in your attic, but those children go through this component.
 *
 * This prop is passed in via the ProjectUsersModal component, if the project users content is meant to be
 * displayed in that modal, but otherwise defaults to false while the project-users-modal feature flag can serve
 * up a false value.
 *
 * The feature flag itself, project-users-modal, is set in components/project/show/infoRows/UsersRow. The hope
 * is that when said flag is cleaned up, removing the now-undesired bits of this component (and children) should be easy.
 */
const ProjectUsersIndex = ({
  id,
  isModal = false,
  projectId,
  openToAssignUsers,
}) => {
  const { showModal } = useModal();
  const [dispatchLoading, setDispatchLoading] = useState(true);
  const [dispatchError, setDispatchError] = useState(false);
  const [project, setProject] = useState(null);
  const dispatch = useDispatch();
  const [userBeingRemoved, setUserBeingRemoved] = useState(null);
  const { showToast, resetToast } = useModalToast();

  const deactivatedArray = [];
  const contributorsArray = [];

  /**
   * if this component is displayed via a modal, there's no longer an id provided from useParams()
   * but the modal trigger will provde a projectId via props
   */
  const navProjectId = id ? parseInt(id, 10) : projectId;
  const projectNodeId = graphQLIdFromProjectDBID(navProjectId);
  const {
    projectUsers,
    error: projectusersError,
    refetch,
    hasNextPage,
    fetchNextPage,
    networkStatus,
  } = useProjectUsers({
    projectId: projectNodeId,
  });

  const { createProjectUsers } = useProjectUserCreate({
    projectId: projectNodeId,
  });
  const onUndoUnassign = () => {
    if (userBeingRemoved) {
      resetToast();
      createProjectUsers({
        variables: {
          input: {
            projectId: projectNodeId,
            userIds: [userBeingRemoved.id],
          },
        },
      });
    }
  };

  const currentUser = useCurrentUser();
  const currentUserCanUnassign = hasRoleAccess(currentUser, [ADMIN, MANAGER]);
  const { projectUserDelete } = useProjectUserDelete({
    projectId: projectNodeId,
    onSuccess: () => {
      Steven.elementClicked({
        id: 'project_user_unassigned',
        content: 'projects.users.unassigned',
        type: ELEMENT_TYPES.BUTTON,
        action: 'project_user_unassigned',
      });

      if (isModal) {
        showToast({
          type: 'success',
          message: translate('projects.users.unassign.success'),
          actionMessage: translate('projects.users.unassign.undo'),
          onActionPress: onUndoUnassign,
          toastDuration: 3000,
        });
      } else {
        toast.success(translate('projects.users.unassign.success'));
      }
    },
    onError: () => {},
  });

  const doProjectFetch = useCallback(() => {
    setDispatchLoading(true);
    refetch();
    dispatch(fetchProject(navProjectId))
      .then((_project) => {
        setProject(_project);
        setDispatchError(false);
      })
      .catch(() => {
        setProject(null);
        setDispatchError(true);
      })
      .finally(() => setDispatchLoading(false));
  }, [dispatch, navProjectId, refetch]);

  useEffect(doProjectFetch, [navProjectId]);

  const onSubmit = () => {
    /**
     * If on the Project Users screen, refetch needs to be called after new users are added
     * via the Assign Users modal. If on the Project Users modal, a new Project Users modal is displayed
     * after the user-add so calling refetch is unnecessary (and throws an error!).
     */
    if (!isModal) {
      refetch();
    }
  };

  const onUnassign = (projectUserId, projectUser) => {
    setUserBeingRemoved(projectUser.user);
    projectUserDelete({
      variables: {
        input: {
          projectUserId,
        },
      },
    });
  };

  const openAssignUsersModal = () =>
    showModal(Modal.ADD_PROJECT_USERS, {
      project,
      onSubmit,
    });

  useEffect(() => {
    if (openToAssignUsers) {
      openAssignUsersModal();
    }
  });

  const renderError = () => {
    return (
      <Alert flavor="destroy" iconName="alert" showBorder>
        <ErrorContent>
          <ErrorMessage>
            {translate('projects.users.errorMessage')}
          </ErrorMessage>
          <ErrorAction>
            <Button color="destroy" size="small" onClick={doProjectFetch}>
              {translate('projects.users.tryAgain')}
            </Button>
          </ErrorAction>
        </ErrorContent>
      </Alert>
    );
  };

  const renderMemberList = () => {
    if (isModal) {
      projectUsers.forEach((user) => {
        if (user.assignedUser.status === USER_DELETED_STATUS) {
          deactivatedArray.push(user);
        } else {
          contributorsArray.push(user);
        }
      });
    }

    return (
      <Layout>
        {!isModal && (
          <>
            <Header>
              <Intro>
                {translate('projects.users.listItem.introPara', {
                  projectName: project.title,
                })}
                :
              </Intro>

              <CollabCallout>
                {translate('projects.users.anotherCompany')}
                <CollabCalloutLinkTooltip>
                  <CollabCalloutLink to="collaboration">
                    {translate('projects.users.collabCta')}
                  </CollabCalloutLink>
                  <CollabCalloutTooltip
                    className="hint--left hint--large"
                    aria-label={translate('projects.users.collabTooltip')}
                  >
                    <CollabCalloutInfoIcon className="mdi mdi-information" />
                  </CollabCalloutTooltip>
                </CollabCalloutLinkTooltip>
              </CollabCallout>
            </Header>
            <ProjectUserList>
              {projectUsers.map((user) => (
                <ProjectUserListItem key={user.id}>
                  <ProjectUser
                    user={user}
                    handleUnassign={(projectUserId) =>
                      onUnassign(projectUserId, user)
                    }
                    showUnassignButton={currentUserCanUnassign}
                  />
                </ProjectUserListItem>
              ))}
            </ProjectUserList>
          </>
        )}
        {isModal ? (
          <>
            <GroupingHeader
              title={translate('projects.users.contributors')}
              assignUserButton={currentUserCanUnassign}
              project={project}
              onSubmit={onSubmit}
            />
            <ProjectUserList>
              {contributorsArray.map((user) => (
                <ProjectUserListItem key={user.id}>
                  <ProjectUser
                    user={user}
                    handleUnassign={(projectUserId) =>
                      onUnassign(projectUserId, user)
                    }
                    showUnassignButton={currentUserCanUnassign}
                  />
                </ProjectUserListItem>
              ))}
            </ProjectUserList>
            {deactivatedArray.length > 0 ? (
              <GroupingHeader
                title={translate('projects.users.deactivated')}
                assignUserButton={false}
                project={project}
              />
            ) : null}
            <ProjectUserList>
              {deactivatedArray.map((user) => (
                <ProjectUserListItem key={user.id}>
                  <ProjectUser
                    user={user}
                    handleUnassign={(projectUserId) =>
                      onUnassign(projectUserId, user)
                    }
                    showUnassignButton={currentUserCanUnassign}
                  />
                </ProjectUserListItem>
              ))}
            </ProjectUserList>
          </>
        ) : null}
        {networkStatus === NetworkStatus.fetchMore && <Spinner />}{' '}
        {hasNextPage ? (
          <LoadMoreContainer>
            <Button type="B" onClick={async () => fetchNextPage()}>
              {translate('projects.users.loadMore')}
            </Button>
          </LoadMoreContainer>
        ) : null}
        <div>
          {hasRoleAccess(currentUser, [ADMIN, MANAGER]) && !isModal ? (
            <ListItemAddButton
              onClick={openAssignUsersModal}
              contentBefore={
                <AssignUserActionAddAvatar>
                  <AssignUserActionIcon className="mdi mdi-plus" />
                </AssignUserActionAddAvatar>
              }
            >
              {translate('projects.members.add.title')}
            </ListItemAddButton>
          ) : null}
        </div>
      </Layout>
    );
  };

  if (
    dispatchLoading ||
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch
  ) {
    return <Spinner />;
  }

  const renderCta = () => {
    if (isModal || hasRoleAccess(currentUser, [STANDARD, RESTRICTED])) {
      return null;
    }

    return (
      <Button color="primary" onClick={openAssignUsersModal}>
        {translate('projects.users.cta')}
      </Button>
    );
  };

  return (
    <ProjectSubpage
      projectId={navProjectId}
      projectTitle={project.title}
      subpageTitle={translate('projects.users.title')}
      cta={renderCta()}
      isModal={isModal}
    >
      {dispatchError || projectusersError ? renderError() : renderMemberList()}
    </ProjectSubpage>
  );
};

ProjectUsersIndex.propTypes = {
  id: PropTypes.string,
  isModal: PropTypes.bool,
  openToAssignUsers: PropTypes.bool,
  projectId: PropTypes.number,
};

export default ProjectUsersIndex;
